import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Device } from "@twilio/voice-sdk";
import {
  Phone, PhoneCall, PhoneOff, Pause, Play, SkipForward, Loader2, ListChecks, CheckCircle2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DISPOSITIONS = [
  "Answered", "No Answer", "Busy", "Voicemail", "Callback",
  "Not Interested", "DNC", "Transfer/Completed",
];
const ADVANCE_DELAY_MS = 3000;

export default function PowerDialer() {
  const [queue, setQueue] = useState(null);
  const [queueBusy, setQueueBusy] = useState(false);
  const [session, setSession] = useState(null);
  const [current, setCurrent] = useState(null);   // {call_log_id, followup, phone}
  const [calling, setCalling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disp, setDisp] = useState({ disposition: "", notes: "", callback_date: "" });
  const deviceRef = useRef(null);
  const connRef = useRef(null);
  const advanceTimerRef = useRef(null);

  const loadQueue = () => {
    setQueueBusy(true);
    api.get("/power-dialer/queue").then(({ data }) => setQueue(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load your dialer queue"))
      .finally(() => setQueueBusy(false));
  };
  useEffect(() => { loadQueue(); }, []);

  const cleanupDevice = () => {
    try { connRef.current?.disconnect(); } catch (e) {}
    try { deviceRef.current?.destroy(); } catch (e) {}
    connRef.current = null;
    deviceRef.current = null;
    setCalling(false);
  };

  useEffect(() => () => {
    cleanupDevice();
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
  }, []);

  const startBrowserCall = async (phone, callLogId) => {
    try {
      const { data: t } = await api.post("/voice/token");
      const device = new Device(t.token, { logLevel: 1 });
      deviceRef.current = device;
      device.on("error", () => cleanupDevice());
      const conn = await device.connect({ params: { To: phone, CallLogId: callLogId } });
      connRef.current = conn;
      setCalling(true);
      ["disconnect", "cancel", "reject", "error"].forEach((ev) => conn.on(ev, () => cleanupDevice()));
    } catch (err) {
      cleanupDevice();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Call could not start");
    }
  };

  const advance = async (sid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/power-dialer/session/${sid}/next`);
      setSession(data.session);
      if (data.done) {
        setCurrent(null);
        toast.success(`Queue complete — ${data.session.completed_count} call(s) logged`);
        loadQueue();
        return;
      }
      setCurrent({ call_log_id: data.call_log_id, followup: data.followup, phone: data.followup.phone });
      setDisp({ disposition: "", notes: "", callback_date: "" });
      await startBrowserCall(data.followup.phone, data.call_log_id);
    } catch (err) {
      if (err.response?.status !== 409) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not advance the dialer queue");
      }
    } finally {
      setBusy(false);
    }
  };

  const startDialing = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/power-dialer/session/start", {});
      setSession(data);
      await advance(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start the power dialer");
      setBusy(false);
    }
  };

  const pause = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/power-dialer/session/${session.id}/pause`);
      setSession(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const resume = async () => {
    if (!session) return;
    try {
      const { data } = await api.post(`/power-dialer/session/${session.id}/resume`);
      setSession(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const skip = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/power-dialer/session/${session.id}/skip`);
      setSession(data.session);
      if (data.done) {
        setCurrent(null);
        toast.success("Queue complete");
        loadQueue();
      } else {
        setCurrent({ call_log_id: data.call_log_id, followup: data.followup, phone: data.followup.phone });
        setDisp({ disposition: "", notes: "", callback_date: "" });
        await startBrowserCall(data.followup.phone, data.call_log_id);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not skip this record");
    } finally {
      setBusy(false);
    }
  };

  const endSession = async () => {
    cleanupDevice();
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (session) {
      try { await api.post(`/power-dialer/session/${session.id}/end`); } catch (e) {}
    }
    setSession(null);
    setCurrent(null);
    loadQueue();
  };

  const hangUp = () => cleanupDevice();

  const saveDisposition = async () => {
    if (!disp.disposition) { toast.error("Select a disposition"); return; }
    setBusy(true);
    try {
      await api.post(`/power-dialer/calls/${current.call_log_id}/disposition`, disp);
      toast.success(`Logged: ${disp.disposition}`);
      setCurrent(null);
      advanceTimerRef.current = setTimeout(() => advance(session.id), ADVANCE_DELAY_MS);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save disposition");
    } finally {
      setBusy(false);
    }
  };

  if (!queue) {
    return <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const active = session && session.status !== "completed" && session.status !== "stopped";
  const awaitingDisposition = current && !calling;

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="power-dialer">
      <div className="overline mb-3 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Client Follow-Up</div>
      <p className="text-xs text-muted-foreground -mt-2 mb-3">Current clients &amp; policyholders — payment reminders, servicing, and retention. Separate from Auto Dialer's cold-lead queue.</p>

      {!active ? (
        <div className="space-y-3 max-w-lg">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="secondary" data-testid="pd-eligible-count">{queue.eligible_count} ready to call</Badge>
            <Badge variant="outline" data-testid="pd-total-count">{queue.total_count} in your queue</Badge>
            <Button variant="ghost" size="sm" onClick={loadQueue} disabled={queueBusy}>Refresh</Button>
          </div>
          {queue.eligible_count === 0 ? (
            <p className="text-sm text-muted-foreground">No callable records in your assigned queue right now.</p>
          ) : (
            <Button onClick={startDialing} disabled={busy} data-testid="pd-start" className="brand-action">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PhoneCall className="mr-1.5 h-4 w-4" />}
              Start Dialing
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="pd-counters">
            <Badge variant="secondary">{session.completed_count} completed</Badge>
            <Badge variant="outline">{session.remaining_count} remaining</Badge>
            {session.skipped_count > 0 && <Badge variant="outline">{session.skipped_count} skipped</Badge>}
            <span className={`ml-auto inline-flex items-center gap-1.5 text-xs ${session.status === "paused" ? "text-amber-600" : "text-emerald-600"}`}>
              <span className={`h-2 w-2 rounded-full ${session.status === "paused" ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`} />
              {session.status === "paused" ? "Paused" : "Dialing"}
            </span>
          </div>

          {current && (
            <div className="rounded-md border border-border bg-background p-4 space-y-1.5" data-testid="pd-current-record">
              <div className="font-medium">{current.followup.client_name || "Unknown client"}</div>
              <div className="text-sm text-muted-foreground font-mono">{current.followup.phone}</div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                {current.followup.carrier && <span>Carrier: {current.followup.carrier}</span>}
                {current.followup.policy_number && <span>Policy: {current.followup.policy_number}</span>}
                {current.followup.followup_type && <span>{current.followup.followup_type}</span>}
              </div>
              {current.followup.next_action && <p className="text-xs text-muted-foreground pt-1">{current.followup.next_action}</p>}
            </div>
          )}

          {calling && (
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700" data-testid="pd-call-in-progress">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Call in progress
              </div>
              <Button variant="destructive" size="sm" onClick={hangUp} data-testid="pd-end-call">
                <PhoneOff className="mr-1.5 h-4 w-4" /> End Call
              </Button>
            </div>
          )}

          {awaitingDisposition && (
            <div className="max-w-lg space-y-3" data-testid="pd-disposition-form">
              <p className="text-sm font-medium">Wrap up the call — disposition is required.</p>
              <Select value={disp.disposition} onValueChange={(v) => setDisp({ ...disp, disposition: v })}>
                <SelectTrigger data-testid="pd-disp-select"><SelectValue placeholder="Outcome" /></SelectTrigger>
                <SelectContent>{DISPOSITIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              {disp.disposition === "Callback" && (
                <Input type="date" value={disp.callback_date} onChange={(e) => setDisp({ ...disp, callback_date: e.target.value })} data-testid="pd-disp-callback-date" />
              )}
              <Textarea placeholder="Notes (optional)" value={disp.notes} onChange={(e) => setDisp({ ...disp, notes: e.target.value })} />
              <Button onClick={saveDisposition} disabled={busy} data-testid="pd-disp-save">
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                Save &amp; Continue
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            {session.status === "active" ? (
              <Button variant="outline" onClick={pause} disabled={busy} data-testid="pd-pause">
                <Pause className="mr-1.5 h-4 w-4" /> Pause
              </Button>
            ) : (
              <Button variant="outline" onClick={resume} disabled={busy} data-testid="pd-resume">
                <Play className="mr-1.5 h-4 w-4" /> Resume
              </Button>
            )}
            <Button variant="outline" onClick={skip} disabled={busy || calling || awaitingDisposition} data-testid="pd-skip">
              <SkipForward className="mr-1.5 h-4 w-4" /> Skip
            </Button>
            <Button variant="ghost" onClick={endSession} disabled={busy} data-testid="pd-end-session">
              End Session
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
